import MuiAlert, { AlertProps as MuiAlertProps } from '@mui/material/Alert';
import { SxProps, Theme } from '@mui/material/styles';
import { FC } from 'react';
import Box from 'yoda-ui/components/Box';
import { YodaColors, YodaSpacing } from 'yoda-ui/yodaTheme';

type AlertProps = MuiAlertProps & {
  active?: boolean;
  fitContent?: boolean;
  hideDefaultIcon?: boolean;
  action?: unknown;
  sx?: SxProps<Theme>;
};

const useAlertStyle = (fitContent: boolean, sx?: SxProps<Theme>): SxProps<Theme> => ({
  whiteSpace: 'pre-line',
  display: 'flex',
  ...(sx ?? {}),
  ...(fitContent ? {} : {}),
  '&.MuiAlert-standardSuccess': {
    backgroundColor: `${YodaColors.greenSuccessLight}`,
    color: `${YodaColors.greenSuccessDark}`,
    '& .MuiAlert-icon': {
      color: `${YodaColors.greenSuccessDark}`,
    },
  },
  '&.MuiAlert-standardInfo': {
    backgroundColor: `${YodaColors.primaryBlueLightest}`,
    color: `${YodaColors.primaryBlue}`,
    '& .MuiAlert-icon': {
      color: `${YodaColors.primaryBlue}`,
    },
    '& .MuiAlert-message': {
      padding: `${YodaSpacing.none}`,
      color: `${YodaColors.blue7}`,
    },
  },
  '&.MuiAlert-standardError': {
    backgroundColor: `${YodaColors.errorLight}`,
    color: `${YodaColors.error}`,
    '& .MuiAlert-icon': {
      color: `${YodaColors.error}`,
    },
  },
  '&.MuiAlert-standardWarning': {
    backgroundColor: `${YodaColors.warningLight}`,
    color: `${YodaColors.warning}`,
    '& .MuiAlert-icon': {
      color: `${YodaColors.warning}`,
    },
  },
  '& .MuiAlert-icon': {
    paddingTop: '0.75rem',
    paddingBottom: YodaSpacing.xxSmall,
    marginRight: YodaSpacing.xxSmall,
  },
  '& .MuiAlert-action': {
    paddingTop: YodaSpacing.xxSmall,
  },
  '& svg': {
    width: '16px',
    height: '16px',
  },
  '& *': {
    color: YodaColors.inherit,
  },
});

const Alert: FC<AlertProps> = ({ severity, active = true, fitContent = false, hideDefaultIcon, children, action, sx }) => {
  const isActive = severity && active;
  const alertStyle = useAlertStyle(fitContent, sx);
  const iconProp = hideDefaultIcon ? { icon: false } : null;

  return (
    <Box>
      {
        isActive
          ? (
            <MuiAlert severity={ severity } action={ action } sx={ alertStyle } { ...iconProp }>
              { children }
            </MuiAlert>
          )
          : null
      }
    </Box>
  );
};

export default Alert;
